import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

const Footer = () => {
  return (
    <footer className="bg-slate-50 dark:bg-[#020617] pt-20 pb-10 relative overflow-hidden border-t border-slate-200 dark:border-white/5 transition-colors duration-500">
      {/* Ambient Background Glow - Theme Adaptive */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent/5 blur-[150px] rounded-full pointer-events-none mix-blend-multiply dark:mix-blend-normal"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {/* Brand Column */}
          <div className="md:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
               {/* Logo - Invert logic for light mode */}
              <img
                src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png"
                alt="EverestCloud"
                className="h-10 w-10 object-contain dark:brightness-0 dark:invert brightness-0 invert-0" />

              <div className="flex flex-col">
                <span className="font-bold text-2xl tracking-tight text-slate-900 dark:text-white leading-none">
                    Everest<span className="text-accent">Cloud</span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mt-1 font-medium">
                    Scalable. Reliable. Nepali.
                </span>
              </div>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed max-w-sm font-light">
              Nepal’s Sovereign Enterprise Cloud Platform. Engineered for the strict data residency needs of banks, government, and regulated industries.
            </p>
            <div className="flex gap-2 pt-2">
                <SocialButton icon={<Linkedin className="h-5 w-5" />} />
                <SocialButton icon={<Twitter className="h-5 w-5" />} />
                <SocialButton icon={<Facebook className="h-5 w-5" />} />
            </div>
          </div>

          {/* Links Column 1 - Products */}
          <div className="md:col-span-2 md:col-start-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-6 tracking-wide">Products</h3>
            <ul className="space-y-3">
              <FooterLink to="/products#infrastructure">Compute & Storage</FooterLink>
              <FooterLink to="/products#performance">GPU Infrastructure</FooterLink>
              <FooterLink to="/products#platforms">Cloud Platforms</FooterLink>
              <FooterLink to="/products#protection">Data Protection</FooterLink>
              <FooterLink to="/products#security">Security & Identity</FooterLink>
            </ul>
          </div>

          {/* Links Column 2 - Solutions */}
          <div className="md:col-span-2">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-6 tracking-wide">Solutions</h3>
            <ul className="space-y-3">
              <FooterLink to="/solutions#banking">Banking & Finance</FooterLink>
              <FooterLink to="/solutions#ai">AI & Analytics</FooterLink>
              <FooterLink to="/solutions#resilience">DR & Continuity</FooterLink>
              <FooterLink to="/solutions#migration">Migration Services</FooterLink>
              <FooterLink to="/solutions#security">Security Operations</FooterLink>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="md:col-span-3">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-6 tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 group">
                <MapPin className="h-5 w-5 shrink-0 text-accent group-hover:text-slate-900 dark:group-hover:text-white transition-colors mt-0.5" />
                <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-gray-200 transition-colors">Kathmandu, Nepal<br />Corporate Headquarters</span>
              </li>
              <li className="flex items-center gap-3 group">
                <Phone className="h-5 w-5 shrink-0 text-accent group-hover:text-slate-900 dark:group-hover:text-white transition-colors" />
                <span className="dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-gray-200 transition-colors !text-sm text-slate-600">+977-14537311</span>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="h-5 w-5 shrink-0 text-accent group-hover:text-slate-900 dark:group-hover:text-white transition-colors" />
                <span className="dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-gray-200 transition-colors !text-sm text-slate-600">mailus@digitalnetwork.com.np</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-slate-200 dark:bg-white/10 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 dark:text-slate-500 font-light">
          <p>© {new Date().getFullYear()} EverestCloud. All rights reserved.</p>
          <div className="flex gap-8 mt-4 md:mt-0">
             <Link to="/privacy-policy" className="hover:text-accent transition-colors">Privacy Policy</Link>
             <Link to="/terms-of-service" className="hover:text-accent transition-colors">Terms of Service</Link>
             <Link to="/sla" className="hover:text-accent transition-colors">SLA</Link>
          </div>
        </div>
      </div>
    </footer>);

};

const SocialButton = ({ icon }) =>
<Button variant="ghost" size="icon" className="h-10 w-10 rounded-full bg-slate-200 dark:bg-white/5 hover:bg-accent dark:hover:bg-accent hover:text-white text-slate-500 dark:text-slate-400 transition-all duration-300">
        {icon}
    </Button>;


const FooterLink = ({ to, children }) =>
<li>
        <Link to={to} className="text-sm text-slate-600 dark:text-slate-400 hover:text-accent dark:hover:text-accent transition-all duration-200 flex items-center gap-1 group">
            <span className="w-0 group-hover:w-2 h-[1px] bg-accent transition-all duration-300"></span>
            {children}
        </Link>
    </li>;


export default Footer;